// =============================================================================
// HardwareIDAllocator UI — Endpoint Functions
// =============================================================================
// Thin wrappers around apiRequest, organised per module. React Query hooks
// in hooks/ consume these directly.
// =============================================================================

import { apiRequest, newIdempotencyKey } from "@/lib/api-client";
import type {
  AccessTokenDto,
  AuditPage,
  DashboardDto,
  ComplianceViewDto,
  ContractManufacturerDto,
  CreateCmRequest,
  CreateImeiNamespaceRequest,
  CreateLinkRequest,
  CreateMacNamespaceRequest,
  CreateUnitTypeRequest,
  CreateUnitSchemeRequest,
  UpdateUnitSchemeRequest,
  CreateTokenRequest,
  CreateTokenResponse,
  CreateUsbPidNamespaceRequest,
  UpdateImeiNamespaceRequest,
  UpdateMacNamespaceRequest,
  UpdateUsbPidNamespaceRequest,
  IdentifierStatus,
  ImeiIssueRequest,
  ImeiIssueResponse,
  ImeiNamespaceDto,
  ImeiValueDto,
  LifecycleActionRequest,
  LinkDto,
  ImeiSearchFilter,
  ImeiSearchPage,
  MacBlockDto,
  MacIssueRequest,
  MacIssueResponse,
  MacNamespaceDto,
  MacSearchFilter,
  MacSearchPage,
  MacValueDto,
  PatchComplianceRequest,
  PatchMetadataRequest,
  UnitTypeDto,
  RotateTokenRequest,
  RotateTokenResponse,
  SeedResultDto,
  UnitBulkIssueRequest,
  UnitBulkIssueResponse,
  UnitConfirmRequest,
  UnitIssueRequest,
  UnitIssueResponse,
  UnitReplaceRequest,
  UnitReplaceResponse,
  UnitReserveRequest,
  UnitReserveResponse,
  UnitSchemeDto,
  UnitSearchPage,
  UnitValueDto,
  AssembleRequest,
  AssemblyDto,
  BomNode,
  UnmountRequestBody,
  UpdateCmRequest,
  UpdateUnitTypeRequest,
  UsbPidIssueRequest,
  UsbPidIssueResponse,
  UsbPidNamespaceDto,
  UsbPidSearchFilter,
  UsbPidSearchPage,
  UsbPidValueDto,
  WhoamiDto,
  MeDto,
  SsoProviderDto,
} from "./types";

// Build a `?a=1&b=foo` query string from a flat object, dropping null/undefined
// entries. Used by the per-namespace search endpoints so each call-site can
// pass a partial filter object without hand-rolling URL encoding.
function qs(params: Record<string, string | number | null | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

// ---- Auth ------------------------------------------------------------------

export const auth = {
  whoami: (overrides?: { apiUrlOverride?: string; tokenOverride?: string }) =>
    apiRequest<WhoamiDto>("/api/v1/auth/whoami", overrides),

  // Cookie-Session-Identität (SSO). Immer credentials:'include' — wird auch im
  // Callback aufgerufen, bevor ein Profil im Store liegt. suppressUnauthorized
  // dort, damit ein 401 ("kein Mitglied") die Callback-Fehler-UI zeigt statt
  // den globalen Session-Expired-Handler.
  me: (overrides?: { apiUrlOverride?: string; suppressUnauthorized?: boolean }) =>
    apiRequest<MeDto>("/api/v1/auth/me", { withCredentials: true, ...overrides }),

  // Aktive SSO-Provider (anonym) — Quelle für die Login-Buttons.
  providers: (overrides?: { apiUrlOverride?: string }) =>
    apiRequest<SsoProviderDto[]>("/api/v1/auth/providers", overrides),

  // Server-Cookie invalidieren (SSO-Logout).
  logout: () =>
    apiRequest<void>("/api/v1/auth/logout", { method: "POST", withCredentials: true }),
};

// ---- UnitTypes --------------------------------------------------------------

export const unitTypes = {
  list: () => apiRequest<UnitTypeDto[]>("/api/v1/unit-types"),
  get: (id: string) => apiRequest<UnitTypeDto>(`/api/v1/unit-types/${id}`),
  create: (body: CreateUnitTypeRequest) =>
    apiRequest<UnitTypeDto>("/api/v1/unit-types", { method: "POST", body }),
  update: (id: string, body: UpdateUnitTypeRequest) =>
    apiRequest<UnitTypeDto>(`/api/v1/unit-types/${id}`, { method: "PATCH", body }),
  delete: (id: string) =>
    apiRequest<void>(`/api/v1/unit-types/${id}`, { method: "DELETE" }),
};

// ---- Contract Manufacturers ------------------------------------------------

export const cms = {
  list: () => apiRequest<ContractManufacturerDto[]>("/api/v1/contract-manufacturers"),
  get: (id: string) =>
    apiRequest<ContractManufacturerDto>(`/api/v1/contract-manufacturers/${id}`),
  create: (body: CreateCmRequest) =>
    apiRequest<ContractManufacturerDto>("/api/v1/contract-manufacturers", {
      method: "POST",
      body,
    }),
  update: (id: string, body: UpdateCmRequest) =>
    apiRequest<ContractManufacturerDto>(`/api/v1/contract-manufacturers/${id}`, {
      method: "PATCH",
      body,
    }),
};

// ---- Tokens ----------------------------------------------------------------

export const tokens = {
  list: () => apiRequest<AccessTokenDto[]>("/api/v1/tokens"),
  get: (id: string) => apiRequest<AccessTokenDto>(`/api/v1/tokens/${id}`),
  create: (body: CreateTokenRequest) =>
    apiRequest<CreateTokenResponse>("/api/v1/tokens", { method: "POST", body }),
  revoke: (id: string) =>
    apiRequest<void>(`/api/v1/tokens/${id}/revoke`, { method: "POST" }),
  rotate: (id: string, body: RotateTokenRequest = {}) =>
    apiRequest<RotateTokenResponse>(`/api/v1/tokens/${id}/rotate`, {
      method: "POST",
      body,
    }),
};

// ---- Audit -----------------------------------------------------------------

export type AuditQuery = {
  class?: string;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export const audit = {
  list: (query: AuditQuery = {}) =>
    apiRequest<AuditPage>("/api/v1/audit", { query }),
};

// ---- Dashboard -------------------------------------------------------------

export const dashboard = {
  /** Single round-trip for the operations cockpit: pool stats, platform counts, recent activity. */
  get: () => apiRequest<DashboardDto>("/api/v1/dashboard"),
};

// ---- Backup ----------------------------------------------------------------

export const backup = {
  /**
   * Returns the raw Response so the caller can stream it as a download.
   * `compress`/`exclude` map straight to query params on
   * GET /api/v1/admin/backup.
   */
  download: (
    opts: { compress?: "gzip" | "none"; exclude?: readonly string[]; signal?: AbortSignal } = {},
  ) =>
    apiRequest<Response>("/api/v1/admin/backup", {
      raw: true,
      signal: opts.signal,
      query: {
        compress: opts.compress,
        exclude: opts.exclude && opts.exclude.length > 0 ? opts.exclude.join(",") : undefined,
      },
    }),
  restore: async (file: File, mode: "replace" | "merge"): Promise<{ rows: number }> => {
    const response = await apiRequest<Response>("/api/v1/admin/restore", {
      method: "POST",
      query: { mode },
      // The backend's restore endpoint auto-detects gzip via magic header,
      // so we don't have to set Content-Encoding even when uploading .gz.
      // Content-Type stays application/x-ndjson for both shapes.
      headers: { "Content-Type": "application/x-ndjson" },
      body: file,
      raw: true,
    });
    const text = await response.text();
    try {
      return JSON.parse(text) as { rows: number };
    } catch {
      return { rows: 0 };
    }
  },
};

// ---- MAC -------------------------------------------------------------------

export const mac = {
  listNamespaces: () =>
    apiRequest<MacNamespaceDto[]>("/api/v1/mac/namespaces"),
  getNamespace: (code: string) =>
    apiRequest<MacNamespaceDto>(`/api/v1/mac/namespaces/${code}`),
  createNamespace: (body: CreateMacNamespaceRequest) =>
    apiRequest<MacNamespaceDto>("/api/v1/mac/namespaces", { method: "POST", body }),
  updateNamespace: (code: string, body: UpdateMacNamespaceRequest) =>
    apiRequest<MacNamespaceDto>(`/api/v1/mac/namespaces/${code}`, { method: "PATCH", body }),
  listBlocks: (code: string) =>
    apiRequest<MacBlockDto[]>(`/api/v1/mac/namespaces/${code}/blocks`),
  createBlock: (code: string, body: { blockByte: string; purpose: string; isActive?: boolean | null }) =>
    apiRequest<MacBlockDto>(`/api/v1/mac/namespaces/${code}/blocks`, { method: "POST", body }),
  updateBlock: (code: string, blockByte: string, body: { purpose?: string | null; isActive?: boolean | null }) =>
    apiRequest<MacBlockDto>(
      `/api/v1/mac/namespaces/${code}/blocks/${encodeURIComponent(blockByte)}`,
      { method: "PATCH", body },
    ),
  deleteBlock: (code: string, blockByte: string) =>
    apiRequest<void>(
      `/api/v1/mac/namespaces/${code}/blocks/${encodeURIComponent(blockByte)}`,
      { method: "DELETE" },
    ),
  issue: (code: string, body: MacIssueRequest, idempotencyKey?: string) =>
    apiRequest<MacIssueResponse>(`/api/v1/mac/namespaces/${code}/issue`, {
      method: "POST",
      body,
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    }),
  getValue: (code: string, value: string) =>
    apiRequest<MacValueDto>(
      `/api/v1/mac/namespaces/${code}/values/${encodeURIComponent(value)}`,
    ),
  searchValues: (code: string, filter: MacSearchFilter = {}) =>
    apiRequest<MacSearchPage>(
      `/api/v1/mac/namespaces/${code}/values${qs({
        status: filter.status,
        category: filter.category,
        issuedFrom: filter.issuedFrom,
        issuedTo: filter.issuedTo,
        q: filter.q,
        page: filter.page,
        pageSize: filter.pageSize,
      })}`,
    ),
  updateValue: (
    code: string,
    value: string,
    body: { reference?: string; note?: string },
  ) =>
    apiRequest<MacValueDto>(
      `/api/v1/mac/namespaces/${code}/values/${encodeURIComponent(value)}`,
      { method: "PATCH", body },
    ),
  lifecycle: (
    code: string,
    value: string,
    action: "confirm-burnt" | "retire" | "release" | "block",
    body: LifecycleActionRequest = {},
  ) =>
    apiRequest<MacValueDto>(
      `/api/v1/mac/namespaces/${code}/values/${encodeURIComponent(value)}/${action}`,
      { method: "POST", body },
    ),
};

// ---- USB-PID ---------------------------------------------------------------

export const usbpid = {
  listNamespaces: () =>
    apiRequest<UsbPidNamespaceDto[]>("/api/v1/usb-pid/namespaces"),
  getNamespace: (code: string) =>
    apiRequest<UsbPidNamespaceDto>(`/api/v1/usb-pid/namespaces/${code}`),
  createNamespace: (body: CreateUsbPidNamespaceRequest) =>
    apiRequest<UsbPidNamespaceDto>("/api/v1/usb-pid/namespaces", {
      method: "POST",
      body,
    }),
  updateNamespace: (code: string, body: UpdateUsbPidNamespaceRequest) =>
    apiRequest<UsbPidNamespaceDto>(`/api/v1/usb-pid/namespaces/${code}`, {
      method: "PATCH",
      body,
    }),
  assign: (code: string, body: UsbPidIssueRequest, idempotencyKey?: string) =>
    apiRequest<UsbPidIssueResponse>(`/api/v1/usb-pid/namespaces/${code}/issue`, {
      method: "POST",
      body,
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    }),
  getValue: (code: string, value: string) =>
    apiRequest<UsbPidValueDto>(
      `/api/v1/usb-pid/namespaces/${code}/values/${encodeURIComponent(value)}`,
    ),
  searchValues: (code: string, filter: UsbPidSearchFilter = {}) =>
    apiRequest<UsbPidSearchPage>(
      `/api/v1/usb-pid/namespaces/${code}/values${qs({
        status: filter.status,
        deviceClass: filter.deviceClass,
        issuedFrom: filter.issuedFrom,
        issuedTo: filter.issuedTo,
        q: filter.q,
        page: filter.page,
        pageSize: filter.pageSize,
      })}`,
    ),
  updateValue: (
    code: string,
    value: string,
    body: { name?: string; deviceClass?: string; reference?: string },
  ) =>
    apiRequest<UsbPidValueDto>(
      `/api/v1/usb-pid/namespaces/${code}/values/${encodeURIComponent(value)}`,
      { method: "PATCH", body },
    ),
  lifecycle: (
    code: string,
    value: string,
    action: "block" | "release",
    body: LifecycleActionRequest = {},
  ) =>
    apiRequest<UsbPidValueDto>(
      `/api/v1/usb-pid/namespaces/${code}/values/${encodeURIComponent(value)}/${action}`,
      { method: "POST", body },
    ),
};

// ---- IMEI ------------------------------------------------------------------

export const imei = {
  listNamespaces: () =>
    apiRequest<ImeiNamespaceDto[]>("/api/v1/imei/namespaces"),
  getNamespace: (code: string) =>
    apiRequest<ImeiNamespaceDto>(`/api/v1/imei/namespaces/${code}`),
  createNamespace: (body: CreateImeiNamespaceRequest) =>
    apiRequest<ImeiNamespaceDto>("/api/v1/imei/namespaces", {
      method: "POST",
      body,
    }),
  updateNamespace: (code: string, body: UpdateImeiNamespaceRequest) =>
    apiRequest<ImeiNamespaceDto>(`/api/v1/imei/namespaces/${code}`, {
      method: "PATCH",
      body,
    }),
  issue: (code: string, body: ImeiIssueRequest, idempotencyKey?: string) =>
    apiRequest<ImeiIssueResponse>(`/api/v1/imei/namespaces/${code}/issue`, {
      method: "POST",
      body,
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    }),
  getValue: (code: string, value: string) =>
    apiRequest<ImeiValueDto>(
      `/api/v1/imei/namespaces/${code}/values/${encodeURIComponent(value)}`,
    ),
  searchValues: (code: string, filter: ImeiSearchFilter = {}) =>
    apiRequest<ImeiSearchPage>(
      `/api/v1/imei/namespaces/${code}/values${qs({
        status: filter.status,
        issuedFrom: filter.issuedFrom,
        issuedTo: filter.issuedTo,
        q: filter.q,
        page: filter.page,
        pageSize: filter.pageSize,
      })}`,
    ),
  lifecycle: (
    code: string,
    value: string,
    action: "confirm-burnt" | "retire" | "release",
    body: LifecycleActionRequest = {},
  ) =>
    apiRequest<ImeiValueDto>(
      `/api/v1/imei/namespaces/${code}/values/${encodeURIComponent(value)}/${action}`,
      { method: "POST", body },
    ),
};

// ---- Units (Werkstücke) ----------------------------------------------------------------

export type UnitSearchQuery = {
  status?: IdentifierStatus;
  issuedFrom?: string;
  issuedTo?: string;
  metadataContains?: string;
  page?: number;
  pageSize?: number;
};

export const units = {
  listSchemas: () => apiRequest<UnitSchemeDto[]>("/api/v1/units/namespaces"),
  getSchema: (code: string) =>
    apiRequest<UnitSchemeDto>(`/api/v1/units/namespaces/${code}`),
  createSchema: (body: CreateUnitSchemeRequest) =>
    apiRequest<UnitSchemeDto>("/api/v1/units/namespaces", {
      method: "POST",
      body,
    }),
  updateSchema: (code: string, body: UpdateUnitSchemeRequest) =>
    apiRequest<UnitSchemeDto>(`/api/v1/units/namespaces/${code}`, {
      method: "PATCH",
      body,
    }),
  seedSchema: (code: string, body: Record<string, unknown> = {}) =>
    apiRequest<SeedResultDto>(`/api/v1/units/namespaces/${code}/seed`, {
      method: "POST",
      body,
    }),
  issue: (code: string, body: UnitIssueRequest, idempotencyKey?: string) =>
    apiRequest<UnitIssueResponse>(`/api/v1/units/namespaces/${code}/issue`, {
      method: "POST",
      body,
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    }),
  bulkIssue: (code: string, body: UnitBulkIssueRequest) =>
    apiRequest<UnitBulkIssueResponse>(
      `/api/v1/units/namespaces/${code}/bulk-issue`,
      { method: "POST", body },
    ),
  search: (code: string, query: UnitSearchQuery = {}) =>
    apiRequest<UnitSearchPage>(
      `/api/v1/units/namespaces/${code}/values`,
      { query },
    ),
  getValue: (code: string, value: string) =>
    apiRequest<UnitValueDto>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}`,
    ),
  reserve: (code: string, body: UnitReserveRequest = {}) =>
    apiRequest<UnitReserveResponse>(
      `/api/v1/units/namespaces/${code}/reserve`,
      { method: "POST", body },
    ),
  confirm: (code: string, value: string, body: UnitConfirmRequest = {}) =>
    apiRequest<UnitValueDto>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/confirm-reservation`,
      { method: "POST", body },
    ),
  releaseReservation: (code: string, value: string) =>
    apiRequest<void>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/release-reservation`,
      { method: "POST", body: {} },
    ),
  replace: (code: string, value: string, body: UnitReplaceRequest) =>
    apiRequest<UnitReplaceResponse>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/replace`,
      { method: "POST", body },
    ),
  lifecycle: (
    code: string,
    value: string,
    action: "confirm-burnt" | "retire" | "release" | "block",
    body: LifecycleActionRequest = {},
  ) =>
    apiRequest<UnitValueDto>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/${action}`,
      { method: "POST", body },
    ),
  patchMetadata: (code: string, value: string, body: PatchMetadataRequest) =>
    apiRequest<{ metadata: string }>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/metadata`,
      { method: "PATCH", body },
    ),
  patchCompliance: (code: string, value: string, body: PatchComplianceRequest) =>
    apiRequest<{ metadata: string }>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/compliance`,
      { method: "PATCH", body },
    ),
  getCompliance: (code: string, value: string) =>
    apiRequest<ComplianceViewDto>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/compliance`,
    ),
  listLinks: (code: string, value: string) =>
    apiRequest<LinkDto[]>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/links`,
    ),
  createLink: (code: string, value: string, body: CreateLinkRequest) =>
    apiRequest<LinkDto>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/links`,
      { method: "POST", body },
    ),
  deleteLink: (code: string, value: string, linkId: string) =>
    apiRequest<void>(
      `/api/v1/units/namespaces/${code}/values/${encodeURIComponent(value)}/links/${linkId}`,
      { method: "DELETE" },
    ),
  byLink: (identifierClass: string, value: string) =>
    apiRequest<LinkDto[]>("/api/v1/units/by-link", {
      query: { class: identifierClass, value },
    }),

  // ---- Assembly / as-built BoM (ADR-0010) ----------------------------------

  /** Mount a child unit into a parent unit at the given slot. */
  assemble: (parentScheme: string, parentSerial: string, body: AssembleRequest) =>
    apiRequest<AssemblyDto>(
      `/api/v1/units/namespaces/${parentScheme}/values/${encodeURIComponent(parentSerial)}/assemble`,
      { method: "POST", body },
    ),

  /** Read the BoM tree of a unit. `recursive=true` walks all sub-components. */
  bom: (scheme: string, serial: string, opts: { recursive?: boolean; includeRemoved?: boolean } = {}) =>
    apiRequest<BomNode[]>(
      `/api/v1/units/namespaces/${scheme}/values/${encodeURIComponent(serial)}/bom`,
      { query: { recursive: opts.recursive, includeRemoved: opts.includeRemoved } },
    ),

  /** Active parent assembly of a unit (null when the unit isn't currently mounted). */
  parents: (scheme: string, serial: string) =>
    apiRequest<AssemblyDto | null>(
      `/api/v1/units/namespaces/${scheme}/values/${encodeURIComponent(serial)}/parents`,
    ),

  /** Soft-delete an assembly link (sets removed_at + removed_reason). */
  unmount: (assemblyId: string, body: UnmountRequestBody) =>
    apiRequest<void>(`/api/v1/units/assemblies/${assemblyId}/unmount`, {
      method: "POST",
      body,
    }),
};
